export interface ThemeColors {
  // Primary brand colors
  primary: {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  };
  
  // Secondary/accent colors
  secondary: {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  };
  
  // Neutral colors
  neutral: {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  };
  
  // Success/error states
  success: {
    50: string;
    500: string;
    600: string;
    700: string;
  };
  
  error: {
    50: string;
    500: string;
    600: string;
    700: string;
  };
  
  warning: {
    50: string;
    500: string;
    600: string;
    700: string;
  };
}

export interface ThemeConfig {
  name: string;
  displayName: string;
  colors: ThemeColors;
  typography: {
    fontFamily: string;
    headingWeight: string;
    bodyWeight: string;
  };
  spacing: {
    section: string;
    card: string;
    element: string;
  };
  borderRadius: {
    small: string;
    medium: string;
    large: string;
    full: string;
  };
  shadows: {
    small: string;
    medium: string;
    large: string;
    glow: string;
  };
  animations: {
    duration: string;
    easing: string;
  };
}

// Healing with Hailey Theme (Natural/Wellness)
export const healingTheme: ThemeConfig = {
  name: 'healing',
  displayName: 'Healing with Hailey',
  colors: {
    primary: {
      50: '#f0fdf4',   // green-50
      100: '#dcfce7',  // green-100
      200: '#bbf7d0',  // green-200
      300: '#86efac',  // green-300
      400: '#4ade80',  // green-400
      500: '#22c55e',  // green-500
      600: '#16a34a',  // green-600
      700: '#15803d',  // green-700
      800: '#166534',  // green-800
      900: '#14532d',  // green-900
    },
    secondary: {
      50: '#f0fdfa',   // teal-50
      100: '#ccfbf1',  // teal-100
      200: '#99f6e4',  // teal-200
      300: '#5eead4',  // teal-300
      400: '#2dd4bf',  // teal-400
      500: '#14b8a6',  // teal-500
      600: '#0d9488',  // teal-600
      700: '#0f766e',  // teal-700
      800: '#115e59',  // teal-800
      900: '#134e4a',  // teal-900
    },
    neutral: {
      50: '#fafaf9',   // stone-50
      100: '#f5f5f4',  // stone-100
      200: '#e7e5e4',  // stone-200
      300: '#d6d3d1',  // stone-300
      400: '#a8a29e',  // stone-400
      500: '#78716c',  // stone-500
      600: '#57534e',  // stone-600
      700: '#44403c',  // stone-700
      800: '#292524',  // stone-800
      900: '#1c1917',  // stone-900
    },
    success: {
      50: '#f0fdf4',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
    },
    error: {
      50: '#fef2f2',
      500: '#ef4444',
      600: '#dc2626',
      700: '#b91c1c',
    },
    warning: {
      50: '#fffbeb',
      500: '#f59e0b',
      600: '#d97706',
      700: '#b45309',
    },
  },
  typography: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    headingWeight: 'font-light',
    bodyWeight: 'font-normal',
  },
  spacing: {
    section: 'py-16',
    card: 'p-8',
    element: 'gap-6',
  },
  borderRadius: {
    small: 'rounded-xl',
    medium: 'rounded-2xl',
    large: 'rounded-3xl',
    full: 'rounded-full',
  },
  shadows: {
    small: 'shadow-sm',
    medium: 'shadow-lg',
    large: 'shadow-2xl',
    glow: 'shadow-green-100/50',
  },
  animations: {
    duration: 'duration-300',
    easing: 'ease-in-out',
  },
};

// Modern/Clinical Theme
export const modernTheme: ThemeConfig = {
  name: 'modern',
  displayName: 'Modern Clinical',
  colors: {
    primary: {
      50: '#eff6ff',   // blue-50
      100: '#dbeafe',  // blue-100
      200: '#bfdbfe',  // blue-200
      300: '#93c5fd',  // blue-300
      400: '#60a5fa',  // blue-400
      500: '#3b82f6',  // blue-500
      600: '#2563eb',  // blue-600
      700: '#1d4ed8',  // blue-700
      800: '#1e40af',  // blue-800
      900: '#1e3a8a',  // blue-900
    },
    secondary: {
      50: '#f8fafc',   // slate-50
      100: '#f1f5f9',  // slate-100
      200: '#e2e8f0',  // slate-200
      300: '#cbd5e1',  // slate-300
      400: '#94a3b8',  // slate-400
      500: '#64748b',  // slate-500
      600: '#475569',  // slate-600
      700: '#334155',  // slate-700
      800: '#1e293b',  // slate-800
      900: '#0f172a',  // slate-900
    },
    neutral: {
      50: '#f9fafb',   // gray-50
      100: '#f3f4f6',  // gray-100
      200: '#e5e7eb',  // gray-200
      300: '#d1d5db',  // gray-300
      400: '#9ca3af',  // gray-400
      500: '#6b7280',  // gray-500
      600: '#4b5563',  // gray-600
      700: '#374151',  // gray-700
      800: '#1f2937',  // gray-800
      900: '#111827',  // gray-900
    },
    success: {
      50: '#ecfdf5',
      500: '#10b981',
      600: '#059669',
      700: '#047857',
    },
    error: {
      50: '#fef2f2',
      500: '#ef4444',
      600: '#dc2626',
      700: '#b91c1c',
    },
    warning: {
      50: '#fffbeb',
      500: '#f59e0b',
      600: '#d97706',
      700: '#b45309',
    },
  },
  typography: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    headingWeight: 'font-semibold',
    bodyWeight: 'font-normal',
  },
  spacing: {
    section: 'py-12',
    card: 'p-6',
    element: 'gap-4',
  },
  borderRadius: {
    small: 'rounded-md',
    medium: 'rounded-lg',
    large: 'rounded-xl',
    full: 'rounded-full',
  },
  shadows: {
    small: 'shadow-sm',
    medium: 'shadow-md',
    large: 'shadow-lg',
    glow: 'shadow-blue-100/50',
  },
  animations: {
    duration: 'duration-200',
    easing: 'ease-out',
  },
};

// Ocean Zen Theme (Blue-green wellness)
export const oceanZenTheme: ThemeConfig = {
  name: 'ocean-zen',
  displayName: 'Ocean Zen',
  colors: {
    primary: {
      50: '#ecfeff',   // cyan-50
      100: '#cffafe',  // cyan-100
      200: '#a5f3fc',  // cyan-200
      300: '#67e8f9',  // cyan-300
      400: '#22d3ee',  // cyan-400
      500: '#06b6d4',  // cyan-500
      600: '#0891b2',  // cyan-600
      700: '#0e7490',  // cyan-700
      800: '#155e75',  // cyan-800
      900: '#164e63',  // cyan-900
    },
    secondary: {
      50: '#f0f9ff',   // sky-50
      100: '#e0f2fe',  // sky-100
      200: '#bae6fd',  // sky-200
      300: '#7dd3fc',  // sky-300
      400: '#38bdf8',  // sky-400
      500: '#0ea5e9',  // sky-500
      600: '#0284c7',  // sky-600
      700: '#0369a1',  // sky-700
      800: '#075985',  // sky-800
      900: '#0c4a6e',  // sky-900
    },
    neutral: {
      50: '#f8fafc',   // slate-50
      100: '#f1f5f9',  // slate-100
      200: '#e2e8f0',  // slate-200
      300: '#cbd5e1',  // slate-300
      400: '#94a3b8',  // slate-400
      500: '#64748b',  // slate-500
      600: '#475569',  // slate-600
      700: '#334155',  // slate-700
      800: '#1e293b',  // slate-800
      900: '#0f172a',  // slate-900
    },
    success: {
      50: '#ecfdf5',
      500: '#10b981',
      600: '#059669',
      700: '#047857',
    },
    error: {
      50: '#fef2f2',
      500: '#ef4444',
      600: '#dc2626',
      700: '#b91c1c',
    },
    warning: {
      50: '#fffbeb',
      500: '#f59e0b',
      600: '#d97706',
      700: '#b45309',
    },
  },
  typography: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    headingWeight: 'font-light',
    bodyWeight: 'font-normal',
  },
  spacing: {
    section: 'py-16',
    card: 'p-8',
    element: 'gap-6',
  },
  borderRadius: {
    small: 'rounded-xl',
    medium: 'rounded-2xl',
    large: 'rounded-3xl',
    full: 'rounded-full',
  },
  shadows: {
    small: 'shadow-sm',
    medium: 'shadow-lg',
    large: 'shadow-2xl',
    glow: 'shadow-cyan-100/50',
  },
  animations: {
    duration: 'duration-300',
    easing: 'ease-in-out',
  },
};

// Lavender Fields Theme (Purple wellness)
export const lavenderTheme: ThemeConfig = {
  name: 'lavender',
  displayName: 'Lavender Fields',
  colors: {
    primary: {
      50: '#faf5ff',   // purple-50
      100: '#f3e8ff',  // purple-100
      200: '#e9d5ff',  // purple-200
      300: '#d8b4fe',  // purple-300
      400: '#c084fc',  // purple-400
      500: '#a855f7',  // purple-500
      600: '#9333ea',  // purple-600
      700: '#7c3aed',  // purple-700
      800: '#6b21a8',  // purple-800
      900: '#581c87',  // purple-900
    },
    secondary: {
      50: '#fdf2f8',   // fuchsia-50
      100: '#fce7f3',  // fuchsia-100
      200: '#fbcfe8',  // fuchsia-200
      300: '#f9a8d4',  // fuchsia-300
      400: '#f472b6',  // fuchsia-400
      500: '#ec4899',  // fuchsia-500
      600: '#db2777',  // fuchsia-600
      700: '#be185d',  // fuchsia-700
      800: '#9d174d',  // fuchsia-800
      900: '#831843',  // fuchsia-900
    },
    neutral: {
      50: '#fefbff',   // very light purple tint
      100: '#faf7ff',
      200: '#f4f1f8',
      300: '#e7e5e8',
      400: '#c9c5d1',
      500: '#8b7fa8',
      600: '#6d5f7a',
      700: '#554961',
      800: '#413749',
      900: '#322a36',
    },
    success: {
      50: '#f0fdf4',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
    },
    error: {
      50: '#fef2f2',
      500: '#ef4444',
      600: '#dc2626',
      700: '#b91c1c',
    },
    warning: {
      50: '#fffbeb',
      500: '#f59e0b',
      600: '#d97706',
      700: '#b45309',
    },
  },
  typography: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    headingWeight: 'font-light',
    bodyWeight: 'font-normal',
  },
  spacing: {
    section: 'py-16',
    card: 'p-8',
    element: 'gap-6',
  },
  borderRadius: {
    small: 'rounded-xl',
    medium: 'rounded-2xl',
    large: 'rounded-3xl',
    full: 'rounded-full',
  },
  shadows: {
    small: 'shadow-sm',
    medium: 'shadow-lg',
    large: 'shadow-2xl',
    glow: 'shadow-purple-100/50',
  },
  animations: {
    duration: 'duration-300',
    easing: 'ease-in-out',
  },
};

// Autumn Harvest Theme (Warm earthy tones)
export const autumnTheme: ThemeConfig = {
  name: 'autumn',
  displayName: 'Autumn Harvest',
  colors: {
    primary: {
      50: '#fefaf5',   // amber-50
      100: '#fef3c7',  // amber-100
      200: '#fde68a',  // amber-200
      300: '#fcd34d',  // amber-300
      400: '#fbbf24',  // amber-400
      500: '#f59e0b',  // amber-500
      600: '#d97706',  // amber-600
      700: '#b45309',  // amber-700
      800: '#92400e',  // amber-800
      900: '#78350f',  // amber-900
    },
    secondary: {
      50: '#fef7ed',   // orange-50
      100: '#fed7aa',  // orange-100
      200: '#fed7aa',  // orange-200
      300: '#fdba74',  // orange-300
      400: '#fb923c',  // orange-400
      500: '#f97316',  // orange-500
      600: '#ea580c',  // orange-600
      700: '#c2410c',  // orange-700
      800: '#9a3412',  // orange-800
      900: '#7c2d12',  // orange-900
    },
    neutral: {
      50: '#fefbf7',   // warm cream
      100: '#fdf6ed',
      200: '#f9ede0',
      300: '#f2e2cd',
      400: '#e6d0b8',
      500: '#d4b896',
      600: '#b89b75',
      700: '#9a7f5c',
      800: '#7d6648',
      900: '#654f39',
    },
    success: {
      50: '#f0fdf4',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
    },
    error: {
      50: '#fef2f2',
      500: '#ef4444',
      600: '#dc2626',
      700: '#b91c1c',
    },
    warning: {
      50: '#fffbeb',
      500: '#f59e0b',
      600: '#d97706',
      700: '#b45309',
    },
  },
  typography: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    headingWeight: 'font-medium',
    bodyWeight: 'font-normal',
  },
  spacing: {
    section: 'py-16',
    card: 'p-8',
    element: 'gap-6',
  },
  borderRadius: {
    small: 'rounded-lg',
    medium: 'rounded-xl',
    large: 'rounded-2xl',
    full: 'rounded-full',
  },
  shadows: {
    small: 'shadow-sm',
    medium: 'shadow-lg',
    large: 'shadow-xl',
    glow: 'shadow-amber-100/50',
  },
  animations: {
    duration: 'duration-300',
    easing: 'ease-in-out',
  },
};

export const themes = {
  healing: healingTheme,
  'ocean-zen': oceanZenTheme,
  lavender: lavenderTheme,
  autumn: autumnTheme,
} as const;

export type ThemeName = keyof typeof themes;